import React, { FunctionComponent } from 'react';
import { WorkflowLogItem, WorkflowLogAction, formattedCurrency, CollapsibleCard } from 'vopay';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'UI';
import styles from './WorkflowChangeHistory.module.scss';

type Props = {
  logs: WorkflowLogItem[];
  loading: boolean;
};

type LogGroup = {
  action: WorkflowLogAction;
  dateAdded: string;
  username: string;
  entries: WorkflowLogItem[];
};

const ACTION_LABELS: Record<WorkflowLogAction, string> = {
  create: 'Created',
  update: 'Modified',
  delete: 'Deleted',
};

function deriveGroupAction(entries: WorkflowLogItem[]): WorkflowLogAction {
  const actions = new Set(entries.map((entry) => entry.Action));
  // Prefer update label when mixed actions exist; only show delete when it's the only action in the group.
  if (actions.has('update')) return 'update';
  if (actions.has('create')) return 'create';
  return 'delete';
}

function parseLogTimestamp(value: string): number {
  const isoLike = value.replace(' ', 'T');
  const parsed = new Date(isoLike).getTime();
  if (!Number.isNaN(parsed)) return parsed;
  return new Date(value).getTime();
}

function sortByMinAmount(entries: WorkflowLogItem[]) {
  return [...entries].sort((a, b) => parseFloat(a.MinAmount) - parseFloat(b.MinAmount));
}

function formatTierLine(
  entry: WorkflowLogItem,
  tierIndex: number,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const currency = t('currencyCode', { defaultValue: 'CAD' });
  const min =
    tierIndex === 0
      ? `${currency} ${formattedCurrency(entry.MinAmount)}`
      : `> ${currency} ${formattedCurrency(entry.MinAmount)}`;
  const max = !entry.MaxAmount
    ? t('noLimit', { defaultValue: 'No limit' })
    : `${currency} ${formattedCurrency(entry.MaxAmount)}`;
  const n = Number(entry.NumberApprovalsRequired);
  return t('tierLine', {
    count: n,
    number: tierIndex + 1,
    min,
    max,
    defaultValue: `Tier {{number}}: {{min}} \u2192 {{max}} \u2022 {{count}} approver(s)`,
  });
}

function isSameTierShape(a: WorkflowLogItem, b: WorkflowLogItem): boolean {
  return (
    a.MinAmount === b.MinAmount &&
    (a.MaxAmount || '') === (b.MaxAmount || '') &&
    Number(a.NumberApprovalsRequired) === Number(b.NumberApprovalsRequired)
  );
}

function findRemovedTierIndexesFromDeleteEntries(
  deleteEntries: WorkflowLogItem[],
  previousEntries: WorkflowLogItem[],
): number[] {
  const sortedPrevious = sortByMinAmount(previousEntries);
  const used = new Set<number>();
  const indexes: number[] = [];

  for (const deleted of deleteEntries) {
    const idx = sortedPrevious.findIndex((prev, i) => !used.has(i) && isSameTierShape(prev, deleted));
    if (idx >= 0) {
      used.add(idx);
      indexes.push(idx);
    }
  }

  return indexes.sort((a, b) => a - b);
}

function buildCurrentEntriesForDiff(group: LogGroup, previousEntries: WorkflowLogItem[]): WorkflowLogItem[] {
  const updateEntries = sortByMinAmount(group.entries.filter((e) => e.Action === 'update'));
  const createEntries = sortByMinAmount(group.entries.filter((e) => e.Action === 'create'));
  const deleteEntries = group.entries.filter((e) => e.Action === 'delete');

  if (previousEntries.length === 0) {
    return sortByMinAmount([...updateEntries, ...createEntries]);
  }

  // Remove deleted tiers from previous snapshot
  const removedIndexes = findRemovedTierIndexesFromDeleteEntries(deleteEntries, previousEntries);
  const removedSet = new Set(removedIndexes);
  const remainingPrevious = sortByMinAmount(previousEntries).filter((_, i) => !removedSet.has(i));

  // Overlay update entries on remaining previous by sorted position.
  // Previous tiers with no corresponding update entry remain unchanged.
  const merged = remainingPrevious.map((prev, i) => updateEntries[i] ?? prev);

  return sortByMinAmount([...merged, ...createEntries]);
}

function groupLogs(logs: WorkflowLogItem[]): LogGroup[] {
  const sorted = [...logs].sort((a, b) => {
    if (a.DateAdded !== b.DateAdded) return a.DateAdded.localeCompare(b.DateAdded);
    return parseInt(a.LogID) - parseInt(b.LogID);
  });

  const groups: LogGroup[] = [];
  for (const log of sorted) {
    const last = groups[groups.length - 1];
    const logTs = parseLogTimestamp(log.DateAdded);
    if (last) {
      const lastEntry = last.entries[last.entries.length - 1];
      const lastTs = parseLogTimestamp(lastEntry.DateAdded);
      // group if within 2 seconds (2000 ms)
      if (
        !Number.isNaN(logTs) &&
        !Number.isNaN(lastTs) &&
        Math.abs(logTs - lastTs) <= 2000 &&
        last.username === log.Username &&
        lastEntry.TransactionType === log.TransactionType
      ) {
        last.entries.push(log);
        last.action = deriveGroupAction(last.entries);
        continue;
      }
    }

    const secondKey = log.DateAdded.substring(0, 19);
    groups.push({ action: deriveGroupAction([log]), dateAdded: secondKey, username: log.Username, entries: [log] });
  }

  return groups.reverse(); // newest first
}

function buildPreviousStates(groups: LogGroup[]): Map<number, WorkflowLogItem[]> {
  const chronGroups = [...groups].reverse();
  const map = new Map<number, WorkflowLogItem[]>();
  let snapshot: WorkflowLogItem[] = [];

  for (let i = 0; i < chronGroups.length; i++) {
    const displayIndex = groups.length - 1 - i;
    map.set(displayIndex, [...snapshot]);
    if (chronGroups[i].action === 'create' || chronGroups[i].action === 'update') {
      snapshot = [...buildCurrentEntriesForDiff(chronGroups[i], snapshot)];
    }
    if (chronGroups[i].action === 'delete') {
      snapshot = [...buildCurrentEntriesForDiff(chronGroups[i], snapshot)];
    }
  }

  return map;
}

type ModifiedDiff = {
  addedLines: string[];
  removedLines: string[];
  changed: Array<{
    newEntry: WorkflowLogItem;
    oldEntry: WorkflowLogItem | undefined;
    tierIndex: number;
    oldTierIndex: number;
  }>;
};

function computeModifiedDiff(
  groupEntries: WorkflowLogItem[],
  previousEntries: WorkflowLogItem[],
  t: (key: string, options?: Record<string, unknown>) => string,
): ModifiedDiff {
  // Split by Action on each entry
  const addedEntries = groupEntries.filter((e) => e.Action === 'create');
  const removedEntries = groupEntries.filter((e) => e.Action === 'delete');
  const updatedEntries = groupEntries.filter((e) => e.Action === 'update');

  // Full sorted current list (non-delete entries) — used for correct tier numbering
  const allCurrentSorted = sortByMinAmount(groupEntries.filter((e) => e.Action !== 'delete'));
  const sortedPrevious = sortByMinAmount(previousEntries);

  const tierLabel = t('tierLabel', { defaultValue: 'Tier' });
  const conjunctionAnd = t('and', { defaultValue: 'and' });

  // Added: use position in the full current list for correct tier number
  const addedLines = sortByMinAmount(addedEntries).map((tier) => {
    const idx = allCurrentSorted.indexOf(tier);
    return t('addedTier', { tierLine: formatTierLine(tier, idx, t), defaultValue: 'Added {{tierLine}}' });
  });

  // Removed: use position in previous list for correct tier number
  const removedIndexes = findRemovedTierIndexesFromDeleteEntries(removedEntries, previousEntries);
  const removedLines: string[] = [];
  if (removedIndexes.length > 0) {
    const names = removedIndexes.map((idx) => `${tierLabel} ${idx + 1}`);
    const tiers =
      names.length === 1 ? names[0] : names.slice(0, -1).join(', ') + ` ${conjunctionAnd} ` + names[names.length - 1];
    removedLines.push(t('removedTiersLine', { tiers, defaultValue: 'Removed {{tiers}}' }));
  }

  // Pair update entries with previous counterparts by sorted position.
  const removedSet = new Set(removedIndexes);
  const remainingPreviousWithIdx = sortedPrevious
    .map((entry, idx) => ({ entry, idx }))
    .filter(({ idx }) => !removedSet.has(idx));
  const sortedUpdated = sortByMinAmount(updatedEntries);

  const changed: ModifiedDiff['changed'] = sortedUpdated
    .map((newEntry, i) => {
      const prev = remainingPreviousWithIdx[i];
      return {
        newEntry,
        oldEntry: prev?.entry,
        tierIndex: allCurrentSorted.indexOf(newEntry),
        oldTierIndex: prev?.idx ?? i,
      };
    })
    .filter(
      ({ newEntry, oldEntry }) =>
        !oldEntry ||
        newEntry.MinAmount !== oldEntry.MinAmount ||
        newEntry.MaxAmount !== oldEntry.MaxAmount ||
        newEntry.NumberApprovalsRequired !== oldEntry.NumberApprovalsRequired,
    );

  return { addedLines, removedLines, changed };
}

function renderDetails(
  group: LogGroup,
  previousEntries: WorkflowLogItem[],
  t: (key: string, options?: Record<string, unknown>) => string,
  groups?: LogGroup[],
  index?: number,
) {
  const currentEntries = buildCurrentEntriesForDiff(group, previousEntries);
  const sortedEntries = sortByMinAmount(currentEntries);
  // Handle activation/deactivation recorded as a "Modified" action where IsActive toggles
  const lastEntry = sortedEntries[sortedEntries.length - 1];

  if (group.action === 'update' && lastEntry) {
    const lastActive = Boolean(lastEntry.IsActive);

    let prevActive: boolean | undefined;

    // Prefer the previous chronological group's last entry when available
    if (groups && typeof index === 'number') {
      const nextGroup = groups[index + 1];
      if (nextGroup) {
        const prevSorted = sortByMinAmount(nextGroup.entries);
        const prevLast = prevSorted[prevSorted.length - 1];
        if (prevLast) prevActive = Boolean(prevLast.IsActive);
      }
    }

    // Fallback to previousEntries snapshot if we couldn't get a previous group
    if (prevActive === undefined && previousEntries && previousEntries.length > 0) {
      const prevSortedSnapshot = sortByMinAmount(previousEntries);
      const prevLastSnapshot = prevSortedSnapshot[prevSortedSnapshot.length - 1];
      if (prevLastSnapshot) prevActive = Boolean(prevLastSnapshot.IsActive);
    }

    if (prevActive !== undefined && lastActive !== prevActive) {
      if (lastActive) {
        return (
          <>
            <div className={styles.detailsTitle}>
              {t('workflowActivatedTitle', { defaultValue: 'Workflow activated' })}
            </div>
            <div className={styles.summaryText}>
              {t('workflowActivatedDescription', {
                defaultValue: 'New transactions on this rail will require approval.',
              })}
            </div>
          </>
        );
      }

      return (
        <>
          <div className={styles.detailsTitle}>
            {t('workflowDeactivatedTitle', { defaultValue: 'Workflow deactivated' })}
          </div>
          <div className={styles.summaryText}>
            {t('workflowDeactivatedDescription', {
              defaultValue: 'All transactions in pending approval status auto-approved.',
            })}
          </div>
        </>
      );
    }
  }
  if (group.action === 'create') {
    return (
      <>
        <div className={styles.detailsTitle}>{t('workflowCreatedTitle', { defaultValue: 'Workflow created' })}</div>
        {sortedEntries.map((entry, i) => (
          <div key={entry.LogID} className={styles.tierLine}>
            {formatTierLine(entry, i, t)}
          </div>
        ))}
      </>
    );
  }

  if (group.action === 'update' || group.action === 'delete') {
    const { addedLines, removedLines, changed } = computeModifiedDiff(group.entries, previousEntries, t);
    const displaySummaryLines = [...addedLines, ...removedLines];
    if (displaySummaryLines.length === 0 && changed.length === 0) return null;

    return (
      <>
        <div className={styles.detailsTitle}>{t('tiersUpdatedTitle', { defaultValue: 'Tier(s) updated' })}</div>
        {displaySummaryLines.map((line, i) => (
          <div key={i} className={styles.summaryText}>
            {line}
          </div>
        ))}
        {changed.length > 0 && (
          <div className={styles.comparisonList}>
            <div className={styles.comparisonBox}>
              <span className={styles.boxLabel}>{t('newLabel', { defaultValue: 'New' })}</span>
              {changed.map(({ newEntry, tierIndex }) => (
                <div className={styles.tierLine} key={newEntry.LogID}>
                  {formatTierLine(newEntry, tierIndex, t)}
                </div>
              ))}
            </div>
            {changed.every(({ oldEntry }) => oldEntry !== undefined) && (
              <div className={styles.comparisonBox}>
                <span className={styles.boxLabel}>{t('oldLabel', { defaultValue: 'Old' })}</span>
                {changed.map(({ newEntry, oldEntry, oldTierIndex }) => (
                  <div className={styles.tierLine} key={`${newEntry.LogID}-old`}>
                    {formatTierLine(oldEntry!, oldTierIndex, t)}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </>
    );
  }

  return null;
}

const WorkflowChangeHistory: FunctionComponent<Props> = ({ logs, loading }) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'TransactionApproval.Configuration.Workflows.Details.ChangeHistory',
  });
  const groups = groupLogs(logs);
  const previousStates = buildPreviousStates(groups);

  return (
    <CollapsibleCard defaultOpen className={styles.card} cardProps={{ hoverEffect: false }}>
      <CollapsibleCard.Trigger
        indicator="chevron"
        style={{ '--collapsibleCardPadding': '24px' } as React.CSSProperties}
      >
        <div>
          <div className={styles.title}>{t('title', { defaultValue: 'Workflow Change History' })}</div>
          <div className={styles.subtitle}>
            {t('subtitle', { defaultValue: "Records all changes made to this workflow's tier configuration." })}
          </div>
        </div>
      </CollapsibleCard.Trigger>

      <CollapsibleCard.Content style={{ '--collapsibleCardPadding': '24px' } as React.CSSProperties}>
        <div className={styles.tableHeader}>
          <span>{t('headers.dateTime', { defaultValue: 'Date & Time' })}</span>
          <span>{t('headers.event', { defaultValue: 'Event' })}</span>
          <span>{t('headers.changedBy', { defaultValue: 'Changed By' })}</span>
          <span>{t('headers.details', { defaultValue: 'Details' })}</span>
        </div>

        {loading ? (
          <div className={styles.skeletonList}>
            <Skeleton height="50px" />
            <Skeleton height="50px" />
          </div>
        ) : (
          groups.map((group, index) => {
            const details = renderDetails(group, previousStates.get(index) ?? [], t, groups, index);
            if (details === null) return null;
            return (
              <div key={`${group.action}-${group.dateAdded}`} className={styles.tableRow}>
                <span className={styles.dateCell}>{group.dateAdded}</span>
                <span className={styles.eventCell}>
                  {t(`events.${group.action}`, { defaultValue: ACTION_LABELS[group.action] })}
                </span>
                <span className={styles.userCell}>{group.username}</span>
                <div>{details}</div>
              </div>
            );
          })
        )}
      </CollapsibleCard.Content>
    </CollapsibleCard>
  );
};

export default WorkflowChangeHistory;
